import { useCallback, useMemo, useRef } from 'react';
import {
  supportLazadaScenarios,
  supportTiktokScenarios,
  supportTokopediaScenarios,
  supportAliexpressScenarios,
  supportMercadoScenarios,
  supportKwaiScenarios,
  supportTiktokReviewScenarios,
  supportFacebookScenarios,
} from 'views/Aide/Scenario/config';
import { isTiKTok, isTiKTokUpperCase } from 'lib/utils/isTikTok';
import { useOrgConfigStateValue } from './useOrgConfig';
import { useRequest } from 'ahooks';
import { getAdminStores } from 'services/api/admin';

const _isComing = (
  scenarioType: Marketing.AllScenarioType,
  platform?: General.PlatformUpperCase,
) => {
  let isComing = false;
  if (platform === undefined) return false;

  isComing = platform === 'LAZADA' ? !supportLazadaScenarios.includes(scenarioType) : isComing;
  isComing = isTiKTokUpperCase(platform)
    ? !supportTiktokScenarios.includes(scenarioType)
    : isComing;
  isComing =
    platform === 'ALIEXPRESS' ? !supportAliexpressScenarios.includes(scenarioType) : isComing;

  if (platform === 'TOKOPEDIA') {
    isComing = !supportTokopediaScenarios.includes(scenarioType);
  }
  isComing = platform === 'MERCADO' ? !supportMercadoScenarios.includes(scenarioType) : isComing;
  isComing = platform === 'KWAI' ? !supportKwaiScenarios.includes(scenarioType) : isComing;
  isComing = platform === 'FACEBOOK' ? !supportFacebookScenarios.includes(scenarioType) : isComing;

  return isComing;
};

const useIsTikTokScenarioTypeComing = () => {
  const { whiteListConfig: { TIKTOK_REVIEW = false } = {} } = useOrgConfigStateValue();

  return (scenarioType: Marketing.AllScenarioType) => {
    return TIKTOK_REVIEW
      ? ![...supportTiktokScenarios, ...supportTiktokReviewScenarios].includes(scenarioType)
      : !supportTiktokScenarios.includes(scenarioType);
  };
};

const useIsComing = (
  scenarioType: Marketing.AllScenarioType,
  platform?: General.PlatformUpperCase,
) => {
  const isTikTokUsLocalSupportScenario =
    scenarioType === 'POSITIVE_REVIEW_CARE' || scenarioType === 'NEGATIVE_REVIEW_SAVE';

  const { data: tiktokUsLocalStore } = useRequest(
    () => {
      return getAdminStores({
        externalType: platform,
        page: 1,
        pageSize: 1,
        storeType: 'LOCAL',
      }).then((res) => res.data.data.stores[0]);
    },
    {
      ready: platform === 'TIKTOK_US' && isTikTokUsLocalSupportScenario,
    },
  );

  const isTikTokScenarioTypeComing = useIsTikTokScenarioTypeComing();

  return useMemo(() => {
    return platform === 'TIKTOK_US' && isTikTokUsLocalSupportScenario
      ? tiktokUsLocalStore === undefined
      : isTiKTok(platform)
      ? isTikTokScenarioTypeComing(scenarioType)
      : _isComing(scenarioType, platform);
  }, [
    isTikTokScenarioTypeComing,
    platform,
    scenarioType,
    isTikTokUsLocalSupportScenario,
    tiktokUsLocalStore,
  ]);
};

export const useCheckIsComing = () => {
  const cacheRef = useRef(new Map());
  const isTikTokScenarioTypeComing = useIsTikTokScenarioTypeComing();

  return useCallback(
    (scenarioType: Marketing.AllScenarioType, platform?: General.PlatformUpperCase) => {
      const key = `${scenarioType}-${platform}`;
      if (cacheRef.current.get(key) !== undefined) {
        return cacheRef.current.get(key);
      }
      const result = isTiKTok(platform)
        ? isTikTokScenarioTypeComing(scenarioType)
        : _isComing(scenarioType, platform);
      cacheRef.current.set(key, result);
      return result;
    },
    [isTikTokScenarioTypeComing],
  );
};

export default useIsComing;
